import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Copy, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import GradeBadge from '@/components/GradeBadge';
import { MarkdownText } from '@/components/MarkdownText';
import { FLUENCY_METRICS, type FluencyScore } from '@/lib/fluency';
import type { AssessmentGrade } from '@/lib/assessment';

// Presentational pieces of an assessment result, shared by the signed-in screens
// (Upload, AssessmentCard) and the free sample (/try).

// STA standards are judged across a range of independent writing; one piece can
// sit above or below the pupil's overall standard, so present this as provisional.
export function ProvisionalGradeNote() {
  return (
    <p className="mt-2 text-xs text-muted-foreground leading-snug">
      This is a best-fit suggestion for this piece only. Writing judgements are made across a range of independent work, so check it against the pupil's other writing before you accept or override it.
    </p>
  );
}

// Grade badge; hovering shows the assessment text, as on assessment cards.
export function GradeWithJustification({
  grade,
  justification,
  size = 'md',
}: {
  grade: AssessmentGrade;
  justification?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <GradeBadge grade={grade} size={size} />
        </span>
      </TooltipTrigger>
      {justification && (
        <TooltipContent
          side="bottom"
          className="z-[100] max-w-sm"
        >
          <MarkdownText variant="tooltip">
            {justification}
          </MarkdownText>
        </TooltipContent>
      )}
    </Tooltip>
  );
}

export function AssessmentTextBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">Assessment</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          onClick={() => {
            navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
          aria-label="Copy assessment text"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <MarkdownText variant="body" className="mt-1">
        {text}
      </MarkdownText>
    </div>
  );
}

export function FluencyScoreGrid({ score }: { score: FluencyScore }) {
  return (
    <div className="w-full pt-1 border-t space-y-3">
      <p className="text-sm font-medium text-muted-foreground">Fluency</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {FLUENCY_METRICS.map(({ key, label, desc }) => (
          <div key={key} className="space-y-1.5">
            <div className="flex justify-between text-xs font-medium">
              <span>{label}</span>
              <span className="text-muted-foreground">{score[key]}/10</span>
            </div>
            <Progress value={score[key] * 10} className="h-2" />
            <p className="text-xs text-muted-foreground leading-snug">{desc}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed w-full border-t pt-2 bg-muted/30 rounded px-2 py-1.5">
        {score.summary}
      </p>
    </div>
  );
}

export function ExtractedTextToggle({
  text,
  open,
  onToggle,
}: {
  text: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-between text-sm text-muted-foreground"
        onClick={onToggle}
      >
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          View Extracted Text
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>
      {open && (
        <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground border rounded-md p-3 bg-muted/30">
          {text}
        </p>
      )}
    </>
  );
}
